/**
 * Crosstab結果をDerived Datasetとして保存する機能
 */

import type { CrosstabDataModel } from './types';
import type { DerivedDataSet, ColumnId, DataValue, DataType, ColumnSchema } from '../../../types';
import { createColumnId, createColumnName, createDatasetId, createTransformOperationId } from '../../../types';
import { markAsTransient } from '../../../types/serialization.types';
import logger from '../logger';

/**
 * Crosstab結果をDerived Dataset形式に変換
 *
 * クロス集計表の形をそのまま保持：
 * - Row Fields（複数可）が最初の列
 * - Column Headersが実際のカラム名
 * - セルの値がそのままデータ
 *
 * 例:
 * Region, Category, Product_A, Product_B, Product_C
 * North,  Electronics, 100,     200,      150
 * South,  Clothing,    50,      75,       25
 */
export function convertCrosstabToDerivedDataset(
  crosstabResult: CrosstabDataModel,
  datasetName: string
): DerivedDataSet {
  const { configuration, structure, data, metadata } = crosstabResult;
  const { rowHeaders, columnHeaders } = structure;
  const { cells } = data;

  logger.info('[SaveAsDataset] Converting crosstab to derived dataset (pivot table format)', {
    name: datasetName,
    rows: rowHeaders.length,
    columns: columnHeaders.length,
    cells: cells.size
  });

  // カラムスキーマを構築
  const columns: ColumnSchema[] = [];

  // Row Fieldsをカラムとして追加
  configuration.rowFields.forEach((rf, index) => {
    columns.push({
      id: createColumnId(`row_${index}_${rf.fieldName}`),
      name: createColumnName(rf.fieldName),
      type: rf.dataType as DataType,
      nullable: false
    });
  });

  // 各Column Headerをカラムとして追加
  // 複数の値フィールドがある場合は、各列ヘッダー × 値フィールドの組み合わせ
  columnHeaders.forEach((colHeader, colIndex) => {
    const colLabel = colHeader.displayValues.join(' / ');

    configuration.valueFields.forEach((vf, vfIndex) => {
      let columnNameStr: string;
      if (configuration.valueFields.length === 1) {
        // 値フィールドが1つの場合は列名のみ
        columnNameStr = colLabel;
      } else {
        // 複数の値フィールドがある場合は「列名_集計関数」
        const aggName = vf.alias || vf.aggregation.toUpperCase();
        columnNameStr = `${colLabel}_${aggName}`;
      }

      columns.push({
        id: createColumnId(`col_${colIndex}_${vfIndex}`),
        name: createColumnName(columnNameStr),
        type: 'float64', // 集計結果なので数値型
        nullable: true // 空セルもあり得る
      });
    });
  });

  // データを構築
  const columnData: Record<ColumnId, DataValue[]> = {};
  columns.forEach(col => {
    columnData[col.id] = [];
  });

  // 各行ヘッダーを1行として追加
  rowHeaders.forEach(rowHeader => {
    // Row Fieldsの値を追加
    const rowValues = JSON.parse(rowHeader.key) as (string | number | null)[];
    rowValues.forEach((val, index) => {
      const colId = columns[index].id;
      columnData[colId].push(val);
    });

    // 各列ヘッダーに対応するセルの値を追加
    const rowFieldCount = configuration.rowFields.length;
    columnHeaders.forEach((colHeader, colIndex) => {
      const cellKey = `row:${rowHeader.key}:col:${colHeader.key}`;
      const cellValue = cells.get(cellKey);

      configuration.valueFields.forEach((_, vfIndex) => {
        const colId = columns[rowFieldCount + colIndex * configuration.valueFields.length + vfIndex].id;

        if (cellValue && !cellValue.isEmpty) {
          columnData[colId].push(cellValue.values[vfIndex]);
        } else {
          columnData[colId].push(null); // 空セルはnull
        }
      });
    });
  });

  const rowCount = rowHeaders.length;

  logger.info('[SaveAsDataset] Converted to dataset (pivot table format)', {
    rowCount,
    columnCount: columns.length
  });

  // DerivedDataSetを構築
  const derivedDataset: DerivedDataSet = {
    id: createDatasetId(`dataset-crosstab-${crypto.randomUUID()}`),
    name: datasetName,
    type: 'derived',
    schema: {
      columns
    },
    rowCount,
    data: columnData,
    parentDatasetIds: [metadata.datasetId],
    operation: {
      id: createTransformOperationId(`transform-${crypto.randomUUID()}`),
      type: 'crosstab',
      targetDatasetId: metadata.datasetId,
      rowFields: configuration.rowFields.map(rf => createColumnName(rf.fieldName)),
      columnFields: configuration.columnFields.map(cf => createColumnName(cf.fieldName)),
      valueFields: configuration.valueFields.map(vf => ({
        field: createColumnName(vf.fieldName),
        aggregation: vf.aggregation,
        alias: vf.alias
      })),
      options: {
        showRowTotals: configuration.options.showRowTotals,
        showColumnTotals: configuration.options.showColumnTotals,
        showGrandTotal: configuration.options.showGrandTotal
      }
    },
    metadata: {
      createdAt: new Date().toISOString(),
      description: `Crosstab result: ${configuration.rowFields.map(f => f.fieldName).join(', ')} × ${configuration.columnFields.map(f => f.fieldName).join(', ')}`,
      basedOn: [metadata.datasetId]
    }
  };

  // dataプロパティをtransientとしてマーク（MDSファイル保存時に除外される）
  markAsTransient(derivedDataset, ['data']);

  return derivedDataset;
}
